export interface ScenarioCalculatorField {
  name: string;
  label: string;
  defaultValue: number;
  unit: string;
  min: number;
  max: number;
  step: number;
}

export interface ScenarioResult {
  label: string;
  formulaKey: string;
  highlight: boolean;
  unit: string;
}

export interface Scenario {
  id: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  heroQuestion: string;
  conceptExplain: string;
  calculatorFields: ScenarioCalculatorField[];
  relatedLinks: { label: string; href: string }[];
  result: ScenarioResult;
}

export const SCENARIOS: Scenario[] = [
  {
    id: 'pension-at-65',
    title: '65세 은퇴 시 국민연금 예상액',
    metaTitle: '65세 은퇴 국민연금 계산기 — 내 연금 예상액 확인 | 노후든든',
    metaDescription: '65세에 은퇴하면 국민연금을 얼마나 받을 수 있을까요? 가입 기간과 소득을 입력하면 예상 월 연금액을 즉시 계산합니다.',
    heroQuestion: '65세에 은퇴하면 국민연금이 얼마나 될까?',
    conceptExplain: '국민연금은 가입 기간이 길수록, 소득이 높을수록 수령액이 늘어납니다. 2024년 기준 A값(전체 가입자 평균소득) 2,861,091원을 기준으로 간이 추정합니다.',
    calculatorFields: [
      { name: 'monthlyIncome', label: '월 소득', defaultValue: 300, unit: '만 원', min: 50, max: 1000, step: 10 },
      { name: 'subscriptionYears', label: '예상 가입 기간', defaultValue: 30, unit: '년', min: 10, max: 45, step: 1 },
    ],
    relatedLinks: [
      { label: '국민연금 계산기 (전체 기능)', href: '/pension' },
      { label: 'ISA·IRP로 연금 보완하기', href: '/tax-benefit' },
    ],
    result: { label: '예상 월 연금액', formulaKey: 'pension-at-65', highlight: true, unit: '원' },
  },
  {
    id: 'fire-30s',
    title: '30대 FIRE 달성 가능할까?',
    metaTitle: '30대 조기 은퇴 FIRE 계산기 — 달성 가능 나이 계산 | 노후든든',
    metaDescription: '30대에 FIRE(경제적 자립·조기 은퇴)가 가능한지 계산해보세요. 현재 자산, 월 투자금, 생활비 입력으로 FIRE 달성 나이를 즉시 확인합니다.',
    heroQuestion: '30대인 나는 몇 살에 FIRE할 수 있을까?',
    conceptExplain: 'FIRE는 Financial Independence, Retire Early의 약자입니다. 핵심은 연간 지출의 25배 자산을 모으는 것, 즉 4% 인출 규칙 기반의 목표선을 넘는 것입니다.',
    calculatorFields: [
      { name: 'currentAge', label: '현재 나이', defaultValue: 32, unit: '세', min: 25, max: 45, step: 1 },
      { name: 'monthlyExpense', label: '월 생활비', defaultValue: 250, unit: '만 원', min: 100, max: 1000, step: 10 },
      { name: 'currentAssets', label: '현재 자산', defaultValue: 3000, unit: '만 원', min: 0, max: 100000, step: 1000 },
      { name: 'monthlyInvestment', label: '월 투자', defaultValue: 150, unit: '만 원', min: 10, max: 5000, step: 10 },
    ],
    relatedLinks: [
      { label: 'FIRE 계산기 (전체 기능)', href: '/fire' },
      { label: '배당 ETF로 FIRE 후 현금흐름 설계', href: '/dividend' },
    ],
    result: { label: '예상 FIRE 달성 나이', formulaKey: 'fire-30s', highlight: true, unit: '세' },
  },
  {
    id: 'retirement-50-billion',
    title: '노후 자금 5억 모으려면?',
    metaTitle: '노후 자금 5억 모으는 법 — 월 납입액 역산 계산기 | 노후든든',
    metaDescription: '노후 자금 5억 원을 모으려면 매달 얼마씩 투자해야 할까요? 목표 금액·기간·수익률을 입력하면 필요 월 납입액을 즉시 계산합니다.',
    heroQuestion: '노후 자금 5억을 모으려면 매달 얼마나 투자해야 할까?',
    conceptExplain: '복리의 힘으로 목표 자산을 역산합니다. 기간이 길수록 필요한 월 납입액이 기하급수적으로 줄어들기 때문에 시작 시점이 가장 중요합니다.',
    calculatorFields: [
      { name: 'targetAmount', label: '목표 자산', defaultValue: 50000, unit: '만 원', min: 1000, max: 500000, step: 1000 },
      { name: 'years', label: '투자 기간', defaultValue: 20, unit: '년', min: 5, max: 40, step: 1 },
      { name: 'expectedReturn', label: '기대 수익률', defaultValue: 7, unit: '%', min: 3, max: 15, step: 0.5 },
    ],
    relatedLinks: [
      { label: '목표 설계 계산기 (전체 기능)', href: '/retirement/goal' },
      { label: '복리 시뮬레이터로 성장 확인', href: '/compound' },
    ],
    result: { label: '필요 월 납입액', formulaKey: 'retirement-50-billion', highlight: true, unit: '원' },
  },
  {
    id: 'severance-10years',
    title: '10년 근무 퇴직금 계산',
    metaTitle: '10년 근무 퇴직금 계산기 — 세전·세후 퇴직금 확인 | 노후든든',
    metaDescription: '10년 근무 후 퇴직금이 얼마인지 계산하세요. 월급과 상여금을 입력하면 법정 퇴직금과 세후 실수령액을 즉시 확인합니다.',
    heroQuestion: '10년 일하면 퇴직금이 얼마나 될까?',
    conceptExplain: '법정 퇴직금은 1일 평균임금 × 30일 × (근속일수 ÷ 365)로 계산합니다. 1년 이상 근무한 모든 근로자가 받을 수 있는 기본 권리입니다.',
    calculatorFields: [
      { name: 'monthlySalary', label: '월 평균 급여', defaultValue: 400, unit: '만 원', min: 100, max: 5000, step: 10 },
      { name: 'annualBonus', label: '연간 상여금', defaultValue: 200, unit: '만 원', min: 0, max: 50000, step: 100 },
    ],
    relatedLinks: [
      { label: '퇴직금 계산기 (전체 기능)', href: '/severance' },
      { label: 'IRP 세제혜택으로 퇴직금 굴리기', href: '/tax-benefit' },
    ],
    result: { label: '예상 퇴직금', formulaKey: 'severance-10years', highlight: true, unit: '원' },
  },
  {
    id: 'dividend-300-monthly',
    title: '배당 ETF로 월 300만원 받으려면?',
    metaTitle: '배당 ETF 월 300만원 수령 — 필요 투자금 계산기 | 노후든든',
    metaDescription: '배당 ETF로 매달 300만 원을 받으려면 얼마나 투자해야 할까요? SCHD, TIGER, JEPI 등 ETF별 목표 달성 시뮬레이션을 확인하세요.',
    heroQuestion: '배당 ETF로 월 300만 원을 받으려면 얼마나 투자해야 할까?',
    conceptExplain: '배당 ETF는 주가 상승과 배당 소득을 동시에 얻는 투자 방법입니다. 배당금을 재투자하면 장기적으로 목표 현금 흐름에 더 빨리 도달할 수 있습니다.',
    calculatorFields: [
      { name: 'targetMonthlyDividend', label: '목표 월 배당금', defaultValue: 300, unit: '만 원', min: 10, max: 2000, step: 10 },
      { name: 'years', label: '투자 기간', defaultValue: 20, unit: '년', min: 5, max: 40, step: 1 },
      { name: 'monthlyAddition', label: '월 추가 투자', defaultValue: 100, unit: '만 원', min: 0, max: 5000, step: 10 },
    ],
    relatedLinks: [
      { label: '배당 ETF 계산기 (전체 기능)', href: '/dividend' },
      { label: 'ISA 계좌로 배당세 절세하기', href: '/tax-benefit' },
    ],
    result: { label: '필요 투자금', formulaKey: 'dividend-300-monthly', highlight: true, unit: '원' },
  },
];
